import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Phone, Check, Star, ChevronRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { EstimateWidget } from "../components/EstimateWidget";
import { ChatWidget } from "../components/ChatWidget";
import { useEstimate } from "../context/EstimateContext";
import { useLenis } from "../hooks/useLenis";
import { CITIES } from "../data/cities";
import { SERVICES, COMPANY } from "../data/site";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function CityDetail() {
  const { city } = useParams();
  const data = CITIES[city];
  const { openEstimate } = useEstimate();
  useLenis();

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : "";
    document.title = data.metaTitle;
    setMeta("description", data.metaDescription);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "city-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      name: `${COMPANY.name} — ${data.name}, PA`,
      description: data.metaDescription,
      telephone: "+1-484-632-5956",
      areaServed: { "@type": "City", name: `${data.name}, Pennsylvania` },
      address: { "@type": "PostalAddress", streetAddress: "801 Broad St", addressLocality: "Wescosville", addressRegion: "PA", postalCode: "18106", addressCountry: "US" },
      url: `https://www.apexhomeproinc.com/remodeling/${city}`,
    });
    document.head.appendChild(ld);
    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      setMeta("description", prevDesc);
      const s = document.getElementById("city-jsonld");
      if (s) s.remove();
    };
  }, [city, data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="bg-[var(--apex-bg)] min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-[84px]">
          <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
            <img src={data.hero} alt={`Home remodeling in ${data.name}, PA by Apex Home Pro`} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--apex-primary)] via-[var(--apex-primary)]/70 to-[var(--apex-primary)]/30" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-10">
              <nav className="flex items-center gap-1.5 text-sm text-slate-300 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight size={14} />
                <a href="/#contact" className="hover:text-white transition-colors">Service Areas</a>
                <ChevronRight size={14} />
                <span className="text-white">{data.name}</span>
              </nav>
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--apex-accent)]">
                <MapPin size={14} /> {data.county}
              </p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white mt-3 max-w-3xl leading-[1.02]"
              >
                Home Remodeling in {data.name}, PA
              </motion.h1>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {data.intro.map((p, i) => (
              <p key={i} className={`text-lg leading-relaxed ${i === 0 ? "text-[var(--apex-primary)] font-medium" : "text-slate-600 mt-5"}`}>{p}</p>
            ))}

            <div className="mt-8 flex items-center gap-3 rounded-md bg-[var(--apex-surface)] border border-slate-200 p-4">
              <div className="flex text-[var(--apex-accent)]">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <span className="text-sm text-slate-600">Rated 4.9 by homeowners across the Lehigh Valley</span>
            </div>

            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">Our services in {data.name}</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {SERVICES.map((s) => (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  data-testid={`city-service-${s.id}`}
                  className="group flex items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white px-4 py-3 hover:border-[var(--apex-accent)] transition-colors"
                >
                  <span className="font-medium text-[var(--apex-primary)]">{s.title}</span>
                  <ArrowRight size={16} className="text-[var(--apex-accent)] -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              ))}
            </div>

            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">Neighborhoods we serve</h2>
            <div className="flex flex-wrap gap-2 mt-5">
              {data.areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--apex-surface)] border border-slate-200 px-3 py-1.5 text-sm text-slate-600">
                  <Check size={13} className="text-[var(--apex-accent)]" /> {a}
                </span>
              ))}
            </div>

            {data.nearby?.length > 0 && (
              <>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">Nearby areas</h2>
                <div className="flex flex-wrap gap-3 mt-5">
                  {data.nearby.map((n) => (
                    <Link key={n} to={`/remodeling/${n}`} data-testid={`nearby-${n}`} className="text-[var(--apex-accent)] font-semibold hover:underline">
                      Remodeling in {CITIES[n]?.name} →
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sticky CTA */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-md bg-[var(--apex-primary)] p-8 text-white">
              <h3 className="font-display font-black text-2xl">Free estimate in {data.name}</h3>
              <p className="text-slate-300 mt-2">Tell us about your project and we'll follow up within one business day.</p>
              <button
                data-testid="city-estimate-btn"
                onClick={openEstimate}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-4 transition-colors"
              >
                Request Free Estimate <ArrowRight size={18} />
              </button>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 hover:border-white/50 font-semibold px-6 py-4 transition-colors">
                <Phone size={18} /> {COMPANY.phone}
              </a>
              <div className="mt-6 pt-6 border-t border-white/10 text-sm text-slate-300">
                <p className="font-semibold text-white">Local & accountable</p>
                <p className="mt-1">Based at {COMPANY.address} · {COMPANY.hours}</p>
              </div>
            </div>
          </aside>
        </section>
      </main>
      <Footer />
      <EstimateWidget />
      <ChatWidget />
    </div>
  );
}
